import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
import "swiper/css/swiper.css";
import Vue from "vue";

import i18n from "@/plugins/i18n";
import BootstrapVue from "bootstrap-vue";
import VueAwesomeSwiper from "vue-awesome-swiper";
import App from "./App";
import router from "./router";
import store from "./store/store";
import Vuex from "vuex";
import VuejsDialog from "vuejs-dialog";
import VueLogger from "vuejs-logger";
import { EMPLOYEE_ROLE } from "./helpers/employee/role";
import { TERMINAL_MODE } from "./helpers/terminal/mode";
import VueAxios from "vue-axios";
import axios from "axios";
import ApiService from "./core/services/api.service";
import indexDbService from "./helpers/dbHelper"
import DashboardService from "./services/dashboard-service";
import liveObjectsService from "./plugins/liveObjects";
import { getAblyApiKey, extractSubdomainFromBaseUrl } from "./helpers/realtimeConfig";
import LazyLoadDirective from './directives/LazyLoadDirective';

const isProduction = process.env.NODE_ENV === "production";

const options = {
  isEnabled: true,
  logLevel: isProduction ? "error" : "debug",
  stringifyArguments: false,
  showLogLevel: true,
  showMethodName: true,
  separator: "|",
  showConsoleColors: true
};

Vue.use(VueLogger, options);

const RSVP = require("rsvp");

Vue.use(RSVP);
Vue.use(BootstrapVue);
Vue.use(VueAwesomeSwiper);
Vue.use(Vuex);
Vue.use(VuejsDialog);
Vue.use(VueAxios, axios);
Vue.use(require("vue-moment"));
Vue.config.productionTip = false;
Vue.config.devtools = true;
Vue.directive('lazy-image', LazyLoadDirective);

indexDbService.initialize()

// reset/stop sync in progress
store.commit("endSync");
// ApiService.init();
let initialSyncDone = false;
router.beforeEach(async function (to, from, next) {
  const isUnlocked = store.getters.isUnlocked;
  const QUICK_SERVICE_ROUTES = [
    "Auth",
    "AuthQuick",
    "Synchronize",
    "Pin",
    "OrdersList",
    "Order",
    "OnlineOrder",
    "OrderCheckout",
    "Shift",
    "Tools",
    "Settings"
  ];
  const KIOSK_MODE_ROUTES = [
    "KioskHomeV2",
    "KioskOrderTypeV2",
    "OrderV2",
    "OrderCheckoutV2",
    "Tools",
    "Synchronize"
  ];
  if (from.path === to.path) return next();
  if (to.name === "Auth") return next();
  else if (to.name === "AuthQuick") return next();
  const baseUrl = localStorage.getItem("baseUrl")
  ApiService.init();
  if (!baseUrl) return next("auth");
  if (!initialSyncDone && localStorage.getItem('connection') === null) {
    initialSyncDone = true;
    return next({ name: "Synchronize" });
  } else if (
    to.name === "Auth"
    || to.name === "AuthQuick"
    || to.name === "Synchronize"
    || to.name === "Reset"
    || to.name === "NewOrderRedirect"
  ) return next();

  const isReconciliationInProgress = store.getters["orders/isRealtimeReconciliationInProgress"];
  if (isReconciliationInProgress && to.name !== "Pin") {
    return next({ name: "Pin" });
  }

  let terminal = store.getters["config/terminal"];
  if (!terminal) {
    await store.dispatch("config/fetchTerminal");
    terminal = store.getters["config/terminal"];
  }
  if (!terminal) {
    return next("auth");
  }
  const storeConfig = store.getters["config/store"];

  if (storeConfig && storeConfig.id) {
    try {
      // Extract subdomain from baseURL instead of store object
      // This will throw an error if baseUrl is invalid - no fallback to "store"
      const extractedSubdomain = extractSubdomainFromBaseUrl(baseUrl);

      await liveObjectsService.initialize({
        apiKey: getAblyApiKey(),
        storeId: storeConfig.id,
        storeSubdomain: extractedSubdomain,
        terminalId: terminal.id
      });
      store.dispatch("realtime/init");
    } catch (error) {
      console.error("[Main] Failed to initialize Ably - subdomain extraction error:", error);
      // Don't initialize Ably if subdomain cannot be extracted
    }
  }
  const terminalMode = TERMINAL_MODE[terminal.mode];
  const isKioskMode = terminalMode === "KIOSK_MODE";

  // Redirect to V2 routes when in Kiosk Mode - ALWAYS use V2
  if (isKioskMode) {
    // Map old kiosk routes to V2 routes
    const routeMapping = {
      "KioskHome": "KioskHomeV2",
      "KioskOrderType": "KioskOrderTypeV2",
      "Order": "OrderV2",
      "OrderCheckout": "OrderCheckoutV2"
    };

    // If trying to access old kiosk route, redirect to V2 (must use next(), not router.push)
    if (routeMapping[to.name]) {
      return next({
        name: routeMapping[to.name],
        params: to.params,
        query: to.query,
        replace: true
      });
    }

    // skip pin for Kiosk Mode - redirect to V2 home if not in allowed routes
    if (!KIOSK_MODE_ROUTES.includes(to.name)) {
      return next({ name: "KioskHomeV2", replace: true });
    }
  }

  if (to.name !== "Pin" && !isUnlocked && terminalMode !== "KIOSK_MODE" && to.name !== "Tools") return next({ name: "Pin" });

  // Check if show_tables db_setting is true in Quick Service mode
  const showTablesInQuickService = terminal && terminal.db_settings
    ? terminal.db_settings.find(setting => setting.name === 'show_tables' && setting.value === 'true')
    : false;

  if (
    terminalMode === "QUICK_SERVICE"
    && !QUICK_SERVICE_ROUTES.includes(to.name)
  ) {
    // Allow Tables, TableOrders, and TablesMoveOrder routes if show_tables is true
    if (showTablesInQuickService && (to.name === "Tables" || to.name === "TableOrders" || to.name === "TablesMoveOrder")) {
      return next();
    }

    return next({
      name: "Order",
      params: {
        tableID: null,
        id: "new"
      },
      replace: true
    });
  }

  if ((to.name === "Order" || to.name === "OrderCheckout")) {
    // store.dispatch("orders/fetchOrdersFromDb")
    let order;
    if (!navigator.onLine || from.name === to.name) {
      order = to.params.id !== "new" ? store.getters["orders/getOrderLocalIdOrById"]((to.params.id)) : null;
    } else if (to.name === "Order") {
      // Validate ID exists before calling API
      if (to.params.id && to.params.id !== "new") {
        // Check if order exists locally and is fresh (within 12 hours) - skip API call if so
        const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
        const localOrder = store.getters["orders/getOrderLocalIdOrById"](to.params.id);
        const orderUpdatedAt = localOrder && localOrder.updated_at ? new Date(localOrder.updated_at).getTime() : 0;
        const isOrderFresh = (Date.now() - orderUpdatedAt) < TWELVE_HOURS_MS;
        const hasCompleteData = localOrder && Array.isArray(localOrder.items) && localOrder.items.length > 0;

        if (isOrderFresh && hasCompleteData) {
          order = localOrder;
        } else {
          try {
            order = (await DashboardService.getOrder(to.params.id)).results[0];
          } catch (error) {
            console.error('[Router] Failed to fetch order:', error.message);
            order = localOrder || null;
          }
        }
      } else {
        order = null;
      }
    }
    const employee = store.getters.employee;
    const dbSettings = store.getters["config/terminal"]["db_settings"];
    if (!dbSettings) return next();
    const allowAllEmployeeOrders = dbSettings.find(setting => setting.name === "allow_all_employee_orders");
    if (
      (allowAllEmployeeOrders && allowAllEmployeeOrders.value === "false") && !isKioskMode
      && employee.role === EMPLOYEE_ROLE.Cashier
      && order && order.emp_open !== employee.id
    ) {
      return store.dispatch("errorModal/displayModalError", {
        modalId: "access-error",
        errorBody: {
          title: i18n.t("access-order-popup.title"),
          info: i18n.t("access-order-popup.text")
        }
      });
    } else if (order && order.id && liveObjectsService.isReady()) {
      // Use presence-based locking - check if another terminal is actively viewing this order
      const viewingTerminalId = await liveObjectsService.getTerminalViewingOrder(order.id, terminal.id);
      if (viewingTerminalId) {
        const store_terminal = store.getters["config/storeTerminals"] || [];
        const locked_by_terminal = store_terminal.find(t => t.pk === viewingTerminalId);
        return store.dispatch("errorModal/displayModalError", {
          modalId: "access-error",
          errorBody: {
            title: i18n.t("locked-order-popup.title"),
            info: `${i18n.t("locked-order-popup.text")}: Terminal #${viewingTerminalId}${locked_by_terminal ? " - " + locked_by_terminal.name : ""}`
          }
        });
      }
    }
  }
  // The split-order table view (TableOrders) lets a terminal edit every split
  // check on a table inline (move items, cancel, pay) without ever routing
  // through "Order"/"OrderCheckout", so it needs its own presence-lock check
  // here rather than relying on the block above.
  if (to.name === "TableOrders" && liveObjectsService.isReady()) {
    const tableIdParam = parseInt(to.params.id);
    if (!Number.isNaN(tableIdParam)) {
      const table = store.getters.getTableWithOrders(tableIdParam);
      const ordersToCheck = (table && table.tableOrders) || [];
      for (const tableOrder of ordersToCheck) {
        const orderKey = tableOrder.id || tableOrder.local_id;
        if (!orderKey) continue;
        const viewingTerminalId = await liveObjectsService.getTerminalViewingOrder(orderKey, terminal.id);
        if (viewingTerminalId) {
          const store_terminal = store.getters["config/storeTerminals"] || [];
          const locked_by_terminal = store_terminal.find(t => t.pk === viewingTerminalId);
          return store.dispatch("errorModal/displayModalError", {
            modalId: "access-error",
            errorBody: {
              title: i18n.t("locked-order-popup.title"),
              info: `${i18n.t("locked-order-popup.text")}: Terminal #${viewingTerminalId}${locked_by_terminal ? " - " + locked_by_terminal.name : ""}`
            }
          });
        }
      }
    }
  }

  // toggle calling of open and recent orders on table view
  if ((to.name === 'Tables' && (from.name !== 'Order' ||  from.name !== 'OrderCheckout')) && terminalMode !== "QUICK_SERVICE") {
      store.dispatch('toggleCallOrderList',true)
  }
  return next();
});

const app = new Vue({
  i18n,
  router,
  store,
  data: {
    syncing: null
  },
  created: async function () {
    if (!("serviceWorker" in navigator) || !("SyncManager" in window)) {
      window.addEventListener("offline", () => {
        store.dispatch("setConnection", false);
      });
      window.addEventListener("online", () => {
        store.dispatch("setConnection", true);
      });
    }
  },
  mounted() {
    window.addEventListener("offline", () => {
      store.dispatch("setConnection", false);
    });

    window.addEventListener("online", () => {
      store.dispatch("setConnection", true);
      // Defer so UI can update first; sync is already deferred in PosLayout.handleNetworkRecovery
      setTimeout(() => {
        store.dispatch("orders/reconcileOrdersWithRealtime");
      }, 0);
    });
  },
  async beforeDestroy () {
    clearInterval(this.syncing);
    await navigator.serviceWorker.removeEventListener("message", this.updateLocalId);
  },
  render: h => h(App)
}).$mount("#app");

if (window.Cypress) {
  window.app = app;
}

document.addEventListener("touchmove", function (event) {
  if (event.scale !== 1) { event.preventDefault(); }
}, false);

document.addEventListener("contextmenu", function (event) {
  event.preventDefault();
}, false);

document.addEventListener("dragstart", function (event) {
  event.preventDefault();
}, false);
